// ═══════════════════════════════════════════════════════════════
// GIFTS SERVICE
// Path: src/services/giftsService.js
//
// All Supabase operations for the gifts table.
// Gifts load ON DEMAND per member — never preloaded into AppContext.
// AppContext donor summary fields (on members table) are updated
// automatically by the recalculate_donor_summary DB trigger.
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

// ── Field mapping: DB snake_case → JS camelCase ───────────────────
const fromDb = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    memberId: row.member_id,
    giftType: row.gift_type,
    amount: row.amount ? parseFloat(row.amount) : null,
    fairMarketValue: row.fair_market_value
      ? parseFloat(row.fair_market_value)
      : null,
    goodsServicesDescription: row.goods_services_description || null,
    deductibleAmount: row.deductible_amount
      ? parseFloat(row.deductible_amount)
      : null,
    giftDate: row.gift_date,
    fundId: row.fund_id || null,
    campaignId: row.campaign_id || null,
    scholarshipId: row.scholarship_id || null,
    paymentMethod: row.payment_method || null,
    checkNumber: row.check_number || null,
    inkindDescription: row.inkind_description || null,
    stockTicker: row.stock_ticker || null,
    stockShares: row.stock_shares ? parseFloat(row.stock_shares) : null,
    stockTransferDate: row.stock_transfer_date || null,
    dafSponsoringOrg: row.daf_sponsoring_org || null,
    dafGrantId: row.daf_grant_id || null,
    acknowledgmentSent: row.acknowledgment_sent || false,
    acknowledgmentDate: row.acknowledgment_date || null,
    acknowledgmentChannel: row.acknowledgment_channel || null,
    receiptNumber: row.receipt_number || null,
    notes: row.notes || null,
    createdBy: row.created_by || null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    // Joined fund name (when fetched with fund relation)
    fund: row.funds
      ? { id: row.funds.id, name: row.funds.name, code: row.funds.code }
      : null,
  };
};

// ── Field mapping: JS camelCase → DB snake_case ───────────────────
const toDb = (data) => {
  const row = {};
  if (data.memberId !== undefined) row.member_id = data.memberId;
  if (data.giftType !== undefined) row.gift_type = data.giftType;
  if (data.amount !== undefined) row.amount = data.amount;
  if (data.fairMarketValue !== undefined)
    row.fair_market_value = data.fairMarketValue;
  if (data.goodsServicesDescription !== undefined)
    row.goods_services_description = data.goodsServicesDescription;
  // deductible_amount is a generated column — never written directly
  if (data.giftDate !== undefined) row.gift_date = data.giftDate;
  if (data.fundId !== undefined) row.fund_id = data.fundId;
  if (data.campaignId !== undefined) row.campaign_id = data.campaignId;
  if (data.scholarshipId !== undefined) row.scholarship_id = data.scholarshipId;
  if (data.paymentMethod !== undefined) row.payment_method = data.paymentMethod;
  if (data.checkNumber !== undefined) row.check_number = data.checkNumber;
  if (data.inkindDescription !== undefined)
    row.inkind_description = data.inkindDescription;
  if (data.stockTicker !== undefined) row.stock_ticker = data.stockTicker;
  if (data.stockShares !== undefined) row.stock_shares = data.stockShares;
  if (data.stockTransferDate !== undefined)
    row.stock_transfer_date = data.stockTransferDate;
  if (data.dafSponsoringOrg !== undefined)
    row.daf_sponsoring_org = data.dafSponsoringOrg;
  if (data.dafGrantId !== undefined) row.daf_grant_id = data.dafGrantId;
  if (data.acknowledgmentSent !== undefined)
    row.acknowledgment_sent = data.acknowledgmentSent;
  if (data.acknowledgmentDate !== undefined)
    row.acknowledgment_date = data.acknowledgmentDate;
  if (data.acknowledgmentChannel !== undefined)
    row.acknowledgment_channel = data.acknowledgmentChannel;
  if (data.notes !== undefined) row.notes = data.notes;
  return row;
};

// ── Query base — always join fund name ────────────────────────────
const GIFT_SELECT = `
  *,
  funds (id, name, code)
`;

export const giftsService = {
  // ── Get all gifts for a member ──────────────────────────────────
  async getByMember(memberId) {
    if (!memberId) return [];
    try {
      const { data, error } = await supabase
        .from("gifts")
        .select(GIFT_SELECT)
        .eq("member_id", memberId)
        .order("gift_date", { ascending: false });

      if (error) throw error;
      return (data || []).map(fromDb);
    } catch (error) {
      debugLog.error("giftsService.getByMember", error);
      throw error;
    }
  },

  // ── Get filtered gifts (for GiftsView) ─────────────────────────
  // filters: { giftType, fundId, startDate, endDate, acknowledged, memberId }
  async getFiltered(filters = {}) {
    try {
      const selectStr = `${GIFT_SELECT}, members (id, first_name, last_name, credentialed_full_name)`;

      // Cursor-paginate by id to avoid Supabase 1000-row default limit
      let allData = [];
      let lastId = null;
      const pageSize = 1000;

      while (true) {
        let query = supabase
          .from("gifts")
          .select(selectStr)
          .order("id", { ascending: true })
          .limit(pageSize);

        if (lastId) query = query.gt("id", lastId);
        if (filters.memberId) query = query.eq("member_id", filters.memberId);
        if (filters.giftType) query = query.eq("gift_type", filters.giftType);
        if (filters.fundId) query = query.eq("fund_id", filters.fundId);
        if (filters.startDate) query = query.gte("gift_date", filters.startDate);
        if (filters.endDate) query = query.lte("gift_date", filters.endDate);
        if (filters.acknowledged !== undefined)
          query = query.eq("acknowledgment_sent", filters.acknowledged);
        if (filters.limit) {
          query = query.limit(filters.limit);
        }

        const { data, error } = await query;
        if (error) throw error;
        if (!data || data.length === 0) break;
        allData = allData.concat(data);
        lastId = data[data.length - 1].id;
        if (filters.limit || data.length < pageSize) break;
      }

      return allData.map((row) => ({
        ...fromDb(row),
        memberName: row.members
          ? row.members.credentialed_full_name ||
            `${row.members.first_name} ${row.members.last_name}`.trim()
          : null,
        memberFirstName: row.members?.first_name || null,
        memberLastName: row.members?.last_name || null,
      }));
    } catch (error) {
      debugLog.error("giftsService.getFiltered", error);
      throw error;
    }
  },

  // ── Get single gift by ID ───────────────────────────────────────
  async getById(giftId) {
    if (!giftId) return null;
    try {
      const { data, error } = await supabase
        .from("gifts")
        .select(GIFT_SELECT)
        .eq("id", giftId)
        .single();

      if (error) throw error;
      return fromDb(data);
    } catch (error) {
      debugLog.error("giftsService.getById", error);
      throw error;
    }
  },

  // ── Create gift ─────────────────────────────────────────────────
  // DB trigger fires recalculate_donor_summary automatically.
  async create(giftData, userId) {
    try {
      const row = {
        ...toDb(giftData),
        created_by: userId,
      };

      const { data, error } = await supabase
        .from("gifts")
        .insert(row)
        .select(GIFT_SELECT)
        .single();

      if (error) throw error;
      debugLog.action("giftsService.create", { giftType: giftData.giftType });
      return fromDb(data);
    } catch (error) {
      debugLog.error("giftsService.create", error);
      throw error;
    }
  },

  // ── Update gift ─────────────────────────────────────────────────
  // DB trigger fires recalculate_donor_summary automatically.
  async update(giftId, updates) {
    if (!giftId) throw new Error("giftId required for update");
    try {
      const { data, error } = await supabase
        .from("gifts")
        .update(toDb(updates))
        .eq("id", giftId)
        .select(GIFT_SELECT)
        .single();

      if (error) throw error;
      debugLog.action("giftsService.update", { giftId });
      return fromDb(data);
    } catch (error) {
      debugLog.error("giftsService.update", error);
      throw error;
    }
  },

  // ── Delete gift ─────────────────────────────────────────────────
  // DB trigger fires recalculate_donor_summary automatically.
  async remove(giftId) {
    if (!giftId) throw new Error("giftId required for delete");
    try {
      const { error } = await supabase.from("gifts").delete().eq("id", giftId);

      if (error) throw error;
      debugLog.action("giftsService.remove", { giftId });
    } catch (error) {
      debugLog.error("giftsService.remove", error);
      throw error;
    }
  },

  // ── Generate receipt number ─────────────────────────────────────
  // Calls the DB function generate_receipt_number(uuid).
  // Immutable once assigned — safe to call multiple times.
  async generateReceiptNumber(giftId) {
    if (!giftId) throw new Error("giftId required");
    try {
      const { data, error } = await supabase.rpc("generate_receipt_number", {
        p_gift_id: giftId,
      });

      if (error) throw error;
      debugLog.action("giftsService.generateReceiptNumber", {
        giftId,
        receiptNumber: data,
      });
      return data; // returns the receipt number string
    } catch (error) {
      debugLog.error("giftsService.generateReceiptNumber", error);
      throw error;
    }
  },

  // ── Mark as acknowledged ────────────────────────────────────────
  // Sets acknowledgment_sent, date, channel.
  // Used by GiftReceiptModal after print or email action.
  async markAcknowledged(giftId, channel = "email") {
    if (!giftId) throw new Error("giftId required");
    try {
      const { data, error } = await supabase
        .from("gifts")
        .update({
          acknowledgment_sent: true,
          acknowledgment_date: new Date().toISOString().slice(0, 10),
          acknowledgment_channel: channel,
        })
        .eq("id", giftId)
        .select(GIFT_SELECT)
        .single();

      if (error) throw error;
      debugLog.action("giftsService.markAcknowledged", { giftId, channel });
      return fromDb(data);
    } catch (error) {
      debugLog.error("giftsService.markAcknowledged", error);
      throw error;
    }
  },

  // ── Bulk mark acknowledged ──────────────────────────────────────
  // Used by GiftsView bulk action (mailed receipts, etc.)
  async bulkMarkAcknowledged(giftIds, channel = "mail") {
    if (!giftIds?.length) return;
    try {
      const { error } = await supabase
        .from("gifts")
        .update({
          acknowledgment_sent: true,
          acknowledgment_date: new Date().toISOString().slice(0, 10),
          acknowledgment_channel: channel,
        })
        .in("id", giftIds);

      if (error) throw error;
      debugLog.action("giftsService.bulkMarkAcknowledged", {
        count: giftIds.length,
        channel,
      });
    } catch (error) {
      debugLog.error("giftsService.bulkMarkAcknowledged", error);
      throw error;
    }
  },

  // ── Get unacknowledged count (for MainView snapshot) ───────────
  // Reads from members.acknowledgment_pending — no gifts query needed.
  async getUnacknowledgedCount() {
    try {
      const { count, error } = await supabase
        .from("members")
        .select("id", { count: "exact", head: true })
        .eq("acknowledgment_pending", true);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      debugLog.error("giftsService.getUnacknowledgedCount", error);
      return 0;
    }
  },
};
